import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { Sidebar } from '../../shared/sidebar/sidebar';
import { SessionService } from '../../core/services/session.service';
import { Sport, SportImage } from '../../core/models/session.model';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-sports',
  imports: [CommonModule, FormsModule, DragDropModule, Sidebar],
  templateUrl: './sports.html',
  styleUrl: './sports.scss',
})
export class Sports implements OnInit {
  sports: Sport[] = [];
  isLoading = false;
  uploadingSportId: string | null = null;
  uploadingGallerySportId: string | null = null;
  successMessage = '';
  errorMessage = '';
  showAddModal = false;
  newSportName = '';
  newSportDescription = '';
  isCreating = false;
  editingDescriptionId: string | null = null;
  editDescriptionValue = '';

  constructor(private sessionService: SessionService) {}

  ngOnInit() {
    this.loadSports();
  }

  loadSports() {
    this.isLoading = true;
    this.sessionService.getSports().subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response.success) {
          this.sports = response.data;
          this.sports.forEach(sport => this.loadGallery(sport));
        }
      },
      error: () => {
        this.isLoading = false;
        this.errorMessage = 'Failed to load sports';
      }
    });
  }

  loadGallery(sport: Sport) {
    this.sessionService.getSportDetail(sport.id).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          sport.galleryImages = response.data.galleryImages;
        }
      }
    });
  }

  startEditDescription(sport: Sport) {
    this.editingDescriptionId = sport.id;
    this.editDescriptionValue = sport.description || '';
  }

  cancelEditDescription() {
    this.editingDescriptionId = null;
    this.editDescriptionValue = '';
  }

  saveDescription(sport: Sport) {
    const desc = this.editDescriptionValue.trim();
    this.sessionService.updateSport(sport.id, { description: desc || undefined }).subscribe({
      next: (response) => {
        if (response.success) {
          sport.description = desc || null;
          this.editingDescriptionId = null;
          this.successMessage = 'Description updated';
          setTimeout(() => this.successMessage = '', 3000);
        }
      },
      error: () => {
        this.errorMessage = 'Failed to update description';
        setTimeout(() => this.errorMessage = '', 3000);
      }
    });
  }

  onGalleryFileSelected(event: Event, sport: Sport) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];

    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      this.errorMessage = 'Only PNG and JPG files are allowed';
      setTimeout(() => this.errorMessage = '', 3000);
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      this.errorMessage = 'File size must be less than 10MB';
      setTimeout(() => this.errorMessage = '', 3000);
      return;
    }

    this.uploadingGallerySportId = sport.id;

    this.sessionService.uploadGalleryImage(sport.id, file).subscribe({
      next: (response) => {
        this.uploadingGallerySportId = null;
        if (response.success && response.data) {
          if (!sport.galleryImages) sport.galleryImages = [];
          sport.galleryImages.push(response.data);
          this.successMessage = 'Gallery image uploaded';
          setTimeout(() => this.successMessage = '', 3000);
        }
      },
      error: (error) => {
        this.uploadingGallerySportId = null;
        this.errorMessage = error.error?.message || 'Failed to upload gallery image';
        setTimeout(() => this.errorMessage = '', 3000);
      }
    });

    input.value = '';
  }

  deleteGalleryImage(sport: Sport, image: SportImage) {
    this.sessionService.deleteGalleryImage(sport.id, image.id).subscribe({
      next: (response) => {
        if (response.success) {
          sport.galleryImages = sport.galleryImages?.filter(gi => gi.id !== image.id);
          this.successMessage = 'Gallery image deleted';
          setTimeout(() => this.successMessage = '', 3000);
        }
      },
      error: () => {
        this.errorMessage = 'Failed to delete gallery image';
        setTimeout(() => this.errorMessage = '', 3000);
      }
    });
  }

  onGalleryDrop(event: CdkDragDrop<SportImage[]>, sport: Sport) {
    if (!sport.galleryImages || event.previousIndex === event.currentIndex) return;
    moveItemInArray(sport.galleryImages, event.previousIndex, event.currentIndex);
    const imageIds = sport.galleryImages.map(gi => gi.id);
    this.sessionService.reorderGalleryImages(sport.id, imageIds).subscribe({
      error: () => {
        this.errorMessage = 'Failed to save gallery order';
        setTimeout(() => this.errorMessage = '', 3000);
      }
    });
  }

  getGalleryImageUrl(image: SportImage): string {
    return environment.apiUrl.replace(/\/api$/, '') + image.imageUrl;
  }

  getImageUrl(sport: Sport): string {
    if (sport.imageUrl) {
      return environment.apiUrl.replace(/\/api$/, '') + sport.imageUrl;
    }
    return '';
  }

  onFileSelected(event: Event, sport: Sport) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];

    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      this.errorMessage = 'Only PNG and JPG files are allowed';
      setTimeout(() => this.errorMessage = '', 3000);
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      this.errorMessage = 'File size must be less than 10MB';
      setTimeout(() => this.errorMessage = '', 3000);
      return;
    }

    this.uploadingSportId = sport.id;
    this.errorMessage = '';
    this.successMessage = '';

    this.sessionService.uploadSportImage(sport.id, file).subscribe({
      next: (response) => {
        this.uploadingSportId = null;
        if (response.success) {
          sport.imageUrl = response.data ?? null;
          this.successMessage = `Image uploaded for ${sport.name}`;
          setTimeout(() => this.successMessage = '', 3000);
        }
      },
      error: (error) => {
        this.uploadingSportId = null;
        this.errorMessage = error.error?.message || 'Failed to upload image';
        setTimeout(() => this.errorMessage = '', 3000);
      }
    });

    input.value = '';
  }

  toggleActive(sport: Sport) {
    const newValue = !sport.isActive;
    this.sessionService.updateSport(sport.id, { isActive: newValue }).subscribe({
      next: (response) => {
        if (response.success) {
          sport.isActive = newValue;
          this.successMessage = `${sport.name} is now ${newValue ? 'active' : 'inactive'}`;
          setTimeout(() => this.successMessage = '', 3000);
        }
      },
      error: () => {
        this.errorMessage = 'Failed to update sport';
        setTimeout(() => this.errorMessage = '', 3000);
      }
    });
  }

  openAddModal() {
    this.showAddModal = true;
    this.newSportName = '';
    this.newSportDescription = '';
  }

  closeAddModal() {
    this.showAddModal = false;
  }

  createSport() {
    if (!this.newSportName.trim()) {
      this.errorMessage = 'Sport name is required';
      setTimeout(() => this.errorMessage = '', 3000);
      return;
    }

    this.isCreating = true;
    this.sessionService.createSport({
      name: this.newSportName.trim(),
      description: this.newSportDescription.trim() || undefined
    }).subscribe({
      next: (response) => {
        this.isCreating = false;
        if (response.success && response.data) {
          this.sports.push(response.data);
          this.successMessage = `${response.data.name} created successfully`;
          setTimeout(() => this.successMessage = '', 3000);
          this.closeAddModal();
        }
      },
      error: (error) => {
        this.isCreating = false;
        this.errorMessage = error.error?.message || 'Failed to create sport';
        setTimeout(() => this.errorMessage = '', 3000);
      }
    });
  }

  onDrop(event: CdkDragDrop<Sport[]>) {
    if (event.previousIndex === event.currentIndex) return;
    moveItemInArray(this.sports, event.previousIndex, event.currentIndex);
    const sportIds = this.sports.map(s => s.id);
    this.sessionService.reorderSports(sportIds).subscribe({
      next: (response) => {
        if (response.success) {
          this.successMessage = 'Sports order updated';
          setTimeout(() => this.successMessage = '', 3000);
        }
      },
      error: () => {
        this.errorMessage = 'Failed to save order';
        setTimeout(() => this.errorMessage = '', 3000);
      }
    });
  }

  toggleComingSoon(sport: Sport) {
    const newValue = !sport.isComingSoon;
    this.sessionService.updateSport(sport.id, { isComingSoon: newValue }).subscribe({
      next: (response) => {
        if (response.success) {
          sport.isComingSoon = newValue;
          this.successMessage = `${sport.name} "Coming Soon" ${newValue ? 'enabled' : 'disabled'}`;
          setTimeout(() => this.successMessage = '', 3000);
        }
      },
      error: () => {
        this.errorMessage = 'Failed to update sport';
        setTimeout(() => this.errorMessage = '', 3000);
      }
    });
  }
}
